import { OnDestroy, DestroyRef, Injectable, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { 
  Firestore,
  CollectionReference,
  DocumentReference,
  QueryConstraint} from '@angular/fire/firestore';
import { 
  collection, 
  doc, 
  updateDoc, 
  deleteDoc, 
  collectionData,
  setDoc,
  Timestamp,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  endBefore,
  getCountFromServer
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject, Subscription, EMPTY, from, throwError, of, race, timer } from 'rxjs';
import { map, catchError, tap, distinctUntilChanged, debounceTime, take, switchMap, filter } from 'rxjs/operators';
import { AuthService } from '@core/services/auth/auth.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { User } from '@shared/definitions/user';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { ErrorHandlingService } from '@core/services/infra/error-handling.service';
import { WorkspaceService } from '@core/services/auth/workspace.service';
import { deepStripUndefined } from '@shared/utils/firestore-clean';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';
import { ProductAnalyticsService } from '@core/services/infra/product-analytics.service';

/**
 * Collections whose CRUD operations emit product-analytics events. Maps the
 * Firestore collection name to the event-name prefix (`property.created`…).
 * Collections absent from this map emit nothing — keeps internal/bookkeeping
 * collections that also extend BaseCrudService out of the analytics stream.
 */
const ANALYTICS_COLLECTION_PREFIXES: Record<string, string> = {
  properties: 'property',
  tenants: 'tenant',
  contracts: 'contract',
  propertyGroups: 'propertyGroup',
};

/**
 * Base interface that all entities must implement to work with BaseCrudService
 */
export interface BaseEntity {
  createdAt?: Date | Timestamp | string;
  updatedAt?: Date | Timestamp | string;
  workspaceId?: string; // Workspace v2.0: All entities belong to a workspace
  deleted?: boolean;
  deletedAt?: Date | Timestamp | string | null;
}

/**
 * Configuration interface for BaseCrudService
 */
export interface CrudServiceConfig {
  /** Name of the Firestore collection */
  collectionName: string;
  /** Field name for the document ID (e.g., 'propertyUID', 'tenantUID') */
  idField: string;
  /** Whether to enable optimistic updates (default: true) */
  enableOptimisticUpdates?: boolean;
  /** Whether to enable soft deletes (default: false) */
  enableSoftDeletes?: boolean;
  /** Whether to enable performance monitoring (default: false) */
  enablePerformanceMonitoring?: boolean;
  /** Whether to enable error handling with retries (default: true) */
  enableErrorHandling?: boolean;
  /** Custom entity constructor function */
  entityConstructor?: (data: unknown) => unknown;
}

/**
 * Query options for advanced filtering and pagination
 */
export interface QueryOptions {
  /** Field to filter by */
  field?: string;
  /** Operator for filtering */
  operator?: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'array-contains-any' | 'in' | 'not-in';
  /** Value to filter by */
  value?: unknown;
  /** Field to order by */
  orderByField?: string;
  /** Order direction */
  orderDirection?: 'asc' | 'desc';
  /** Limit number of results */
  limitCount?: number;
  /** Start after document (for pagination) */
  startAfterDoc?: unknown;
  /** End before document (for pagination) */
  endBeforeDoc?: unknown;
}

/**
 * Batch operation interface
 */
export interface BatchOperation<T> {
  type: 'create' | 'update' | 'delete';
  id?: string;
  data?: Partial<T>;
}

/**
 * Performance metrics interface
 */
export interface PerformanceMetrics {
  operationType: string;
  duration: number;
  timestamp: Date;
  entityCount?: number;
  success: boolean;
  error?: string;
}

/**
 * @domain Core/Services
 * 
 * Abstract base service for CRUD operations with Firestore.
 * 
 * This service provides:
 * - Standardized CRUD operations (Create, Read, Update, Delete)
 * - User-scoped collection management
 * - Automatic timestamp handling
 * - Optimistic updates with rollback
 * - Reactive data streams with BehaviorSubject
 * - Consistent error handling
 * - Authentication validation
 * - Data cleaning and validation
 * 
 * Usage:
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class PropertyService extends BaseCrudService<Property> {
 *   constructor(
 *     firestore: Firestore,
 *     pathService: FirestorePathService,
 *     authService: AuthService
 *   ) {
 *     super(firestore, pathService, authService, {
 *       collectionName: 'properties',
 *       idField: 'propertyUID',
 *       entityConstructor: (data) => new Property(data)
 *     });
 *   }
 * }
 * ```
 */
@Injectable()
export abstract class BaseCrudService<T extends BaseEntity> implements OnDestroy {
  protected readonly config: Required<CrudServiceConfig>;
  
  // Reactive data streams
  protected entitiesSubject = new BehaviorSubject<T[]>([]);
  public entities$ = this.entitiesSubject.asObservable();

  /**
   * True once the live Firestore listener has delivered its FIRST real
   * snapshot for the current (user, workspace) scope. An empty collection
   * still counts — "hydrated" means the server answered, not that rows exist.
   *
   * Why this exists separately from `entities$`: that BehaviorSubject is
   * seeded with `[]` and re-seeded with `[]` on every scope change, so a
   * `take(1)` on it resolves synchronously with no data. Anything that must
   * wait for the collection to actually arrive — the boot splash's completion
   * gate in particular — has to key on this instead, or it just measures how
   * fast a BehaviorSubject replays.
   *
   * Resets to `false` whenever the listener is torn down (scope change,
   * logout) so the next scope re-arms the gate rather than inheriting the
   * previous workspace's verdict.
   */
  protected hydratedSubject = new BehaviorSubject<boolean>(false);
  public hydrated$ = this.hydratedSubject.asObservable();

  // Internal data storage
  protected entitiesData: T[] = [];
  protected user: User | null = null;
  /**
   * Scope key of the currently-active listener: `${userUID}:${workspaceId}`.
   * The re-fetch guard keys on BOTH user and workspace so an in-app workspace
   * switch (same user, no hard reload) tears down the stale listener and
   * re-subscribes against the new workspace path.
   */
  protected lastFetchedScopeKey: string | null = null;
  
  // Subscriptions management
  protected entitiesSubscription: Subscription = new Subscription();
  protected userSubscription: Subscription = new Subscription();
  
  // Performance monitoring
  protected performanceMetrics: PerformanceMetrics[] = [];
  protected readonly maxMetricsHistory = 100;

  protected readonly authStore = inject(AuthStore);
  protected readonly workspaceStore = inject(WorkspaceStore);
  protected readonly productAnalytics = inject(ProductAnalyticsService);
  protected readonly crudDestroyRef = inject(DestroyRef);

  /**
   * Fire a product-analytics event for an allowlisted entity action.
   * Best-effort and synchronous — the service swallows all errors, so this
   * never affects the CRUD result. No-op for non-allowlisted collections.
   */
  protected trackCrud(action: 'created' | 'updated' | 'deleted', entityId: string): void {
    const prefix = ANALYTICS_COLLECTION_PREFIXES[this.config.collectionName];
    if (!prefix) return;
    this.productAnalytics.track(`${prefix}.${action}`, {
      entityId,
      collection: this.config.collectionName,
    });
  }

  constructor(
    protected firestore: Firestore,
    protected pathService: FirestorePathService,
    protected authService: AuthService,
    config: CrudServiceConfig,
    protected errorHandling?: ErrorHandlingService,
    protected workspaceService?: WorkspaceService
  ) {
    // Set default configuration
    this.config = {
      enableOptimisticUpdates: true,
      enableSoftDeletes: false,
      enablePerformanceMonitoring: false,
      enableErrorHandling: true,
      entityConstructor: (data: unknown) => data as T,
      ...config
    };
    
    this.initializeService();
  }

  ngOnDestroy(): void {
    this.entitiesSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
  }

  // ========================================
  // INITIALIZATION & AUTHENTICATION
  // ========================================

  /**
   * Initialize the service and set up user authentication subscription.
   *
   * Gated on BOTH the authenticated user AND `claimsReady$` — the latter
   * signals that the Firebase ID token now carries the workspace custom
   * claims. Without that gate, fresh signups briefly emit an authenticated
   * user whose token doesn't yet contain workspace claims, so Firestore
   * rejects every workspace-scoped listener with "Missing or insufficient
   * permissions". Returning users flip `claimsReady$` to true during
   * `handleAuthStateChange` (cached token already has claims) so they
   * hit no extra latency.
   */
  /**
   * Key signal that dedupes on userUID/email — matches the legacy
   * `distinctUntilChanged((prev, curr) => prev?.userUID === curr?.userUID && ...)`.
   * Returns a stable string key so signal infrastructure auto-dedupes.
   */
  private readonly authKey = computed(() => {
    const u = this.authStore.user();
    return `${u?.userUID ?? ''}:${u?.email ?? ''}`;
  });

  protected initializeService(): void {
    effect(async () => {
      // Track the change-key + claimsReady; signal auto-dedup handles noise.
      const _key = this.authKey();
      const claimsReady = this.authStore.claimsReady();
      const user = this.authStore.user();
      // Track the current workspace so an in-app workspace switch (same user)
      // re-runs this effect and re-fetches against the new workspace path.
      // MUST be read before the first `await` to register as a dependency.
      const _workspaceId = this.workspaceStore.currentWorkspaceId();

      this.user = user;
      const hasAuthedUser = !!(user && user.userUID);

      if (!hasAuthedUser) {
        this.clearEntities();
        return;
      }

      if (!claimsReady) {
        // Authenticated but workspace claims not yet in the token. Wait
        // for claimsReady to flip true and the effect will re-run.
        return;
      }

      if (this.workspaceService) {
        try {
          await this.workspaceStore.waitForWorkspace(10000);
        } catch (_error) {
          console.error(`❌ ${this.config.collectionName}: Workspace timeout, skipping data fetch`);
          return;
        }
      }
      this.fetchAllEntities();
    });
  }

  /**
   * Check if current user is authenticated
   */
  protected isAuthenticated(): boolean {
    return !!this.authStore.user()?.userUID;
  }

  // ========================================
  // FIRESTORE REFERENCES
  // ========================================

  /**
   * Get the user-scoped collection reference
   */
  protected getCollection(): CollectionReference {
    const path = this.pathService.getWorkspaceCollection(this.config.collectionName);
    return collection(this.firestore, path);
  }

  /**
   * Get a user-scoped document reference
   */
  protected getDocument(documentId: string): DocumentReference {
    const path = this.pathService.getWorkspaceDocument(this.config.collectionName, documentId);
    return doc(this.firestore, path);
  }

  /**
   * Create a new Firestore document ID
   */
  protected createId(): string {
    return doc(collection(this.firestore, 'temp')).id;
  }

  // ========================================
  // PERFORMANCE MONITORING
  // ========================================

  /**
   * Record performance metrics for monitoring
   */
  protected recordPerformanceMetric(
    operationType: string, 
    timestamp: number, 
    entityCount?: number, 
    success: boolean = true, 
    error?: string
  ): void {
    if (!this.config.enablePerformanceMonitoring) return;

    const metric: PerformanceMetrics = {
      operationType,
      duration: Date.now() - timestamp,
      timestamp: new Date(),
      entityCount,
      success,
      error
    };

    this.performanceMetrics.push(metric);

    // Keep only the last N metrics to prevent memory leaks
    if (this.performanceMetrics.length > this.maxMetricsHistory) {
      this.performanceMetrics = this.performanceMetrics.slice(-this.maxMetricsHistory);
    }

    // Log slow operations
    if (metric.duration > 1000) {
      console.warn(`🐌 Slow ${operationType} operation for ${this.config.collectionName}: ${metric.duration}ms`);
    }
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): PerformanceMetrics[] {
    return [...this.performanceMetrics];
  }

  /**
   * Clear performance metrics
   */
  public clearPerformanceMetrics(): void {
    this.performanceMetrics = [];
  }

  // ========================================
  // DATA UTILITIES
  // ========================================

  /**
   * Helper method to add standard timestamps to document data
   */
  protected addTimestamps<U extends Record<string, unknown>>(data: U, isUpdate = false): U & {
    createdAt?: Date | Timestamp;
    updatedAt: Date | Timestamp;
  } {
    const now = new Date();
    const result = { ...data, updatedAt: now };

    if (!isUpdate) {
      (result as Record<string, unknown>)['createdAt'] = now;
    }

    return result;
  }

  /**
   * Clean document data before saving. Removes `undefined` values and functions
   * recursively — INCLUDING `undefined` nested inside objects that sit inside
   * arrays (e.g. a Contact inside `tenant.contacts[]`), which the frontend
   * Firestore SDK would otherwise reject, silently failing the whole write.
   * Empty objects ({}) and null are preserved. Delegates to the shared
   * `deepStripUndefined` so this scrub has a single source of truth.
   */
  protected cleanDocumentData<U extends Record<string, unknown>>(data: U): Partial<U> {
    return deepStripUndefined(data) as Partial<U>;
  }

  /**
   * Convert JavaScript Date objects to Firestore Timestamps
   */
  protected convertDatesToTimestamps(data: Record<string, unknown>): Record<string, unknown> {
    const result = { ...data };
    
    // Common date fields that should be converted
    const dateFields = ['date', 'paymentDate', 'createdAt', 'updatedAt', 'deletedAt'];
    
    dateFields.forEach(field => {
      if (result[field] && result[field] instanceof Date) {
        result[field] = Timestamp.fromDate(result[field]);
      }
    });
    
    return result;
  }

  /**
   * Parse and validate date values (handles Firestore Timestamps)
   */
  protected parseDate(dateValue: unknown): Date | null {
    if (!dateValue) return null;

    // Handle Firestore Timestamp objects
    if (typeof dateValue === 'object' && dateValue !== null && 'toDate' in dateValue && typeof (dateValue as { toDate: () => Date }).toDate === 'function') {
      return (dateValue as { toDate: () => Date }).toDate();
    }

    // Handle string dates or already converted Date objects
    const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue as string | number);

    // Validate the date is not Invalid Date
    return !isNaN(parsedDate.getTime()) ? parsedDate : null;
  }

  /**
   * Parse all date fields in an entity automatically
   * Looks for common date field names and Firestore Timestamps
   */
  protected parseDateFields(entity: Record<string, unknown>): void {
    if (!entity || typeof entity !== 'object') return;

    // Common date field names to check
    const dateFieldNames = [
      'createdAt', 'updatedAt', 'date', 'paymentDate', 'startDate', 'endDate',
      'dueDate', 'completedDate', 'scheduledDate', 'deletedAt', 'lastModified'
    ];

    // Parse known date fields
    dateFieldNames.forEach(fieldName => {
      if (entity[fieldName]) {
        entity[fieldName] = this.parseDate(entity[fieldName]);
      }
    });

    // Also check for any Firestore Timestamp objects in other fields
    Object.keys(entity).forEach(key => {
      const value = entity[key];
      if (value && typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: unknown }).toDate === 'function') {
        entity[key] = this.parseDate(value);
      }
    });
  }

  // ========================================
  // DATA FETCHING
  // ========================================

  /**
   * Fetch all entities from Firestore and set up real-time subscription
   * Optimized to prevent multiple subscriptions and reduce flickering
   */
  protected fetchAllEntities(): void {
    const isAuth = this.isAuthenticated();
    if (!isAuth) {
      return;
    }

    // Scope = (user, workspace). A change in EITHER must re-subscribe against
    // the new workspace path — resolved from the same `currentWorkspaceId`
    // signal the path service reads, so there is no path/scope race.
    const currentUserUID = this.user?.userUID;
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    const scopeKey = currentUserUID ? `${currentUserUID}:${workspaceId ?? ''}` : null;

    // Already subscribed for the same (user, workspace) scope — nothing to do.
    if (this.entitiesSubscription && !this.entitiesSubscription.closed && this.lastFetchedScopeKey === scopeKey) {
      return;
    }

    // Scope changed (different user or workspace) or the listener is gone: drop
    // the stale listener and clear cached entities so the previous workspace's
    // data can never surface before the new listener's first emission.
    if (this.entitiesSubscription && !this.entitiesSubscription.closed) {
      this.entitiesSubscription.unsubscribe();
    }
    this.entitiesData = [];
    this.entitiesSubject.next([]);
    // New scope, nothing from the server yet — re-arm the hydration gate.
    this.hydratedSubject.next(false);
    this.lastFetchedScopeKey = scopeKey;

    try {
      const entitiesCollection = this.getCollection();
      
      this.entitiesSubscription = collectionData(entitiesCollection, { idField: this.config.idField }).pipe(
        // Prevent identical emissions to reduce unnecessary processing
        distinctUntilChanged((prev, curr) => {
          // Quick length check first
          if (prev.length !== curr.length) return false;
          
          // Deep comparison for small arrays, shallow for large ones
          if (prev.length < 50) {
            return JSON.stringify(prev) === JSON.stringify(curr);
          } else {
            // For large arrays, compare just IDs and update timestamps
            return prev.every((prevItem, index) => {
              const currItem = curr[index];
              return prevItem?.[this.config.idField] === currItem?.[this.config.idField] &&
                     prevItem?.['updatedAt'] === currItem?.['updatedAt'];
            });
          }
        }),
        // Debounce to prevent rapid-fire updates during bulk operations
        debounceTime(100),
        // Process entities with better error handling
        map(entities => this.processEntitiesOptimized(entities)),
        // Error handling — handleError logs (and silences permission-denied
        // during auth transitions). Keep this pass-through thin so we don't
        // double-log.
        catchError(error => this.handleError(error)),
        // Add performance monitoring if enabled
        tap(entities => {
          if (this.config.enablePerformanceMonitoring) {
            this.recordPerformanceMetric('fetch', Date.now(), entities.length, true);
          }
        }),
        takeUntilDestroyed(this.crudDestroyRef)
      ).subscribe({
        next: (entities) => {
          // Atomic update to prevent intermediate states
          this.entitiesData = entities;
          this.entitiesSubject.next(this.entitiesData);
          // First real snapshot for this scope — an empty result counts.
          if (!this.hydratedSubject.value) {
            this.hydratedSubject.next(true);
          }
        },
        error: (error) => {
          // catchError above already routed through handleError, which
          // silences permission-denied. Anything reaching here is unexpected.
          if (!isPermissionDeniedError(error)) {
            console.error(`❌ Subscription error for ${this.config.collectionName}:`, error);
          }
        }
      });
    } catch (error) {
      console.error(`❌ Error setting up ${this.config.collectionName} subscription:`, error);
      this.handleError(error);
    }
  }

  /**
   * Optimized entity processing with better performance and error handling
   */
  private processEntitiesOptimized(entities: unknown[]): T[] {
    const startTime = Date.now();
    const processedEntities: T[] = [];
    
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      
      try {
        // Skip null or undefined entities
        if (!entity) {
          console.warn(`⚠️ Skipping null entity at index ${i} in ${this.config.collectionName}`);
          continue;
        }

        // Apply entity constructor if provided
        const constructedEntity = this.config.entityConstructor(entity) as T | null | undefined;

        // Skip if constructor returned null/undefined
        if (!constructedEntity) {
          console.warn(`⚠️ Entity constructor returned null for ${this.config.collectionName} at index ${i}:`, entity);
          continue;
        }

        // Parse all date fields in the entity
        this.parseDateFields(constructedEntity as unknown as Record<string, unknown>);

        processedEntities.push(constructedEntity);
      } catch (error) {
        console.error(`❌ Error processing entity at index ${i} in ${this.config.collectionName}:`, error, entity);
        // Continue processing other entities instead of failing completely
        continue;
      }
    }
    
    const processingTime = Date.now() - startTime;
    if (processingTime > 100) {
      console.warn(`⚠️ Slow entity processing for ${this.config.collectionName}: ${processingTime}ms for ${entities.length} entities`);
    }
    
    return processedEntities;
  }

  /**
   * Get all entities (filtered by soft delete if enabled)
   */
  public getEntities(): Observable<T[]> {
    return this.entities$.pipe(
      // Tombstones (`deleted:true`) must never surface in a list, regardless of
      // the per-collection soft-delete strategy. A collection may HARD-delete on
      // user action (enableSoftDeletes:false) yet still receive soft-deletes from
      // a backend flow — e.g. the billing grace-period cron soft-deletes
      // properties so the removal stays reversible. Always drop tombstones.
      map(entities => entities.filter(entity => !(entity as { deleted?: boolean }).deleted))
    );
  }

  /**
   * Get entity by ID (reactive stream version)
   *
   * Returns an Observable that emits the entity whenever the entities list changes.
   * Does NOT complete - continues listening for updates.
   * Use this when you need real-time updates to the entity.
   */
  public getEntityById$(id: string): Observable<T | null> {
    return this.entities$.pipe(
      map(entities => {
        const entity = entities.find(e => (e as unknown as Record<string, unknown>)[this.config.idField] === id);
        if (entity && this.config.enableSoftDeletes && (entity as { deleted?: boolean }).deleted) {
          return null;
        }
        return entity || null;
      })
    );
  }

  /**
   * Get entity by ID (one-time fetch version)
   *
   * This method properly waits for the entity to appear in the stream,
   * which is important when navigating to a newly created entity before
   * the Firestore real-time listener has emitted the updated data.
   *
   * The method will:
   * 1. If entity is found immediately, return it
   * 2. If entities array is empty (still loading), wait for data to arrive
   * 3. If entities array has data but entity not found, give Firestore time to sync
   * 4. Returns null after timeout if entity still not found
   */
  public getEntityById(id: string): Observable<T | null> {
    // Observable that emits when we find the entity or determine it doesn't exist
    const entityFound$ = this.entities$.pipe(
      // Map each emission to check for the entity
      map(entities => {
        const entity = entities.find(e => (e as unknown as Record<string, unknown>)[this.config.idField] === id);
        return {
          entity: entity || null,
          hasData: entities.length > 0,
          isDeleted: entity && this.config.enableSoftDeletes && (entity as { deleted?: boolean }).deleted
        };
      }),
      // Only emit when we have a conclusive result:
      // - Entity found, OR
      // - We have loaded data (entities.length > 0) meaning entity truly doesn't exist
      filter(result => result.entity !== null || result.hasData),
      // Map to final result
      map(result => {
        if (result.isDeleted) {
          return null;
        }
        return result.entity;
      }),
      take(1)
    );

    // For newly created entities, the Firestore listener might not have synced yet.
    // Give it a second chance by waiting a bit and checking again.
    const entityWithRetry$ = entityFound$.pipe(
      switchMap(entity => {
        if (entity !== null) {
          // Entity found, return it immediately
          return of(entity);
        }
        // Entity not found on first check - wait briefly and try again
        // This handles the race condition when navigating to a newly created entity
        console.log(`🔄 Entity ${id} not found in ${this.config.collectionName}, waiting for Firestore sync...`);
        return timer(300).pipe(
          switchMap(() => this.entities$.pipe(
            take(1),
            map(entities => {
              const entity = entities.find(e => (e as unknown as Record<string, unknown>)[this.config.idField] === id);
              if (entity && this.config.enableSoftDeletes && (entity as { deleted?: boolean }).deleted) {
                return null;
              }
              return entity || null;
            })
          ))
        );
      })
    );

    // Race between finding the entity and a timeout
    // This ensures we don't wait forever if something goes wrong
    const timeout$ = timer(5000).pipe(
      map(() => {
        console.warn(`⏱️ getEntityById timed out waiting for ${this.config.collectionName} ${id}`);
        return null as T | null;
      })
    );

    return race(entityWithRetry$, timeout$);
  }

  // ========================================
  // CRUD OPERATIONS
  // ========================================

  /**
   * Create a new entity
   */
  public createEntity(entityData: Omit<T, typeof this.config.idField>): Observable<string> {
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    try {
      // Generate new entity ID
      const entityId = this.createId();
      
      // Get current workspace ID for workspace v2.0
      const workspaceId = this.workspaceStore.currentWorkspaceId();
      if (!workspaceId) {
        const currentUser = this.authStore.user();
        if (!currentUser?.userUID) {
          return throwError(() => new Error('User authentication or workspace selection required'));
        }
      }
      
      // Prepare entity data with required fields
      const entityToSave = this.cleanDocumentData({
        ...entityData,
        [this.config.idField]: entityId,
        workspaceId: workspaceId || undefined
      });

      // Add timestamps
      const entityWithTimestamps = this.addTimestamps(entityToSave, false);

      // Use setDoc with generated ID for better control
      const entityDoc = this.getDocument(entityId);
      
      const operation = from(setDoc(entityDoc, entityWithTimestamps)).pipe(
        map(() => {
          this.trackCrud('created', entityId);
          return entityId;
        }),
        catchError(this.handleError.bind(this))
      );

      // Apply error handling if enabled
      if (this.config.enableErrorHandling && this.errorHandling) {
        return this.errorHandling.withRetry(
          operation,
          {
            maxRetries: 3,
            baseDelay: 1000
          },
          `createEntity-${this.config.collectionName}`,
          { entityId }
        );
      }

      return operation;
    } catch (error) {
      return throwError(() => error);
    }
  }

  /**
   * Update an existing entity
   */
  public updateEntity(id: string, entityData: Partial<T>): Observable<void> {
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    // Get current user ID for security validation
    const currentUser = this.authStore.user();
    if (!currentUser?.userUID) {
      return throwError(() => new Error('User authentication required'));
    }

    // Find the existing entity
    const existingEntity = this.entitiesData.find(e => (e as unknown as Record<string, unknown>)[this.config.idField] === id);
    if (!existingEntity) {
      return throwError(() => new Error(`${this.config.collectionName} not found`));
    }

    try {
      let originalEntitiesData: T[] = [];
      
      // Optimistic update if enabled
      if (this.config.enableOptimisticUpdates) {
        originalEntitiesData = [...this.entitiesData];
        
        const entityIndex = this.entitiesData.findIndex(e => (e as unknown as Record<string, unknown>)[this.config.idField] === id);
        if (entityIndex !== -1) {
          const updatedEntity = Object.assign({}, this.entitiesData[entityIndex], entityData);
          this.entitiesData[entityIndex] = updatedEntity;
          this.entitiesSubject.next([...this.entitiesData]);
          console.log(`🚀 Optimistic update applied for ${this.config.collectionName}:`, id);
        }
      }

      // Clean and prepare the update data
      const cleanedData = this.cleanDocumentData({
        ...entityData,
        [this.config.idField]: id,
        ownerUID: currentUser.userUID
      });
      const updatedData = this.addTimestamps(cleanedData, true);

      const entityDocRef = this.getDocument(id);

      const operation = from(updateDoc(entityDocRef, updatedData)).pipe(
        map(() => {
          this.trackCrud('updated', id);
        }),
        catchError(error => {
          // Rollback optimistic update on error
          if (this.config.enableOptimisticUpdates && originalEntitiesData.length > 0) {
            console.error(`❌ Backend update failed, rolling back optimistic update:`, error);
            this.entitiesData = originalEntitiesData;
            this.entitiesSubject.next([...this.entitiesData]);
          }
          return throwError(() => error);
        })
      );

      // Apply error handling if enabled
      if (this.config.enableErrorHandling && this.errorHandling) {
        return this.errorHandling.withRetry(
          operation,
          {
            maxRetries: 3,
            baseDelay: 1000
          },
          `updateEntity-${this.config.collectionName}`,
          { entityId: id }
        );
      }

      return operation;
    } catch (error) {
      return throwError(() => error);
    }
  }

  /**
   * Delete an entity (hard delete or soft delete based on configuration)
   */
  public deleteEntity(id: string): Observable<void> {
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    if (this.config.enableSoftDeletes) {
      return this.softDeleteEntity(id);
    } else {
      return this.hardDeleteEntity(id);
    }
  }

  /**
   * Hard delete an entity (permanently removes from Firestore)
   */
  protected hardDeleteEntity(id: string): Observable<void> {
    try {
      // Optimistic update if enabled
      let originalEntitiesData: T[] = [];
      if (this.config.enableOptimisticUpdates) {
        originalEntitiesData = [...this.entitiesData];
        this.entitiesData = this.entitiesData.filter(e => (e as unknown as Record<string, unknown>)[this.config.idField] !== id);
        this.entitiesSubject.next([...this.entitiesData]);
      }

      const entityDocRef = this.getDocument(id);
      
      return from(deleteDoc(entityDocRef)).pipe(
        tap(() => {
          this.trackCrud('deleted', id);
        }),
        catchError(error => {
          // Rollback optimistic update on error
          if (this.config.enableOptimisticUpdates && originalEntitiesData.length > 0) {
            console.error(`Error deleting ${this.config.collectionName}:`, error);
            this.entitiesData = originalEntitiesData;
            this.entitiesSubject.next([...this.entitiesData]);
          }
          return throwError(() => error);
        })
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  /**
   * Soft delete an entity (marks as deleted but keeps in Firestore)
   */
  protected softDeleteEntity(id: string): Observable<void> {
    const updateData = {
      deleted: true,
      deletedAt: new Date()
    } as unknown as Partial<T>;

    return this.updateEntity(id, updateData);
  }

  /**
   * Restore a soft-deleted entity
   */
  public restoreEntity(id: string): Observable<void> {
    if (!this.config.enableSoftDeletes) {
      return throwError(() => new Error('Soft deletes not enabled for this service'));
    }

    const updateData = {
      deleted: false,
      deletedAt: null
    } as unknown as Partial<T>;

    return this.updateEntity(id, updateData);
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Get entity count
   */
  public getEntityCount(): Promise<number> {
    return Promise.resolve(this.entitiesData.length);
  }

  /**
   * Clear all entities (used when user logs out).
   *
   * MUST also unsubscribe the live Firestore listener — otherwise the
   * previous user's snapshot listener stays open after `signOut`, fires
   * once with the workspace path it was scoped to, and the unauthenticated
   * client gets a "Missing or insufficient permissions" error. Across a
   * logout-then-login-as-different-user flow, this manifests as a hung
   * loading screen with permission spam in the console until CTRL+F5.
   */
  protected clearEntities(): void {
    if (this.entitiesSubscription && !this.entitiesSubscription.closed) {
      this.entitiesSubscription.unsubscribe();
      this.entitiesSubscription = new Subscription();
    }
    this.entitiesData = [];
    this.entitiesSubject.next([]);
    this.hydratedSubject.next(false);
    this.lastFetchedScopeKey = null; // Reset the active listener scope
  }

  /**
   * Handle errors consistently.
   *
   * Permission-denied is silenced unconditionally for real-time listeners
   * (this `handleError` runs in `collectionData(...).pipe(catchError)`).
   * It happens in three situations:
   *   1. Logout/login transition — listener fires final permission-denied
   *      before unsubscribe lands. Recovers automatically.
   *   2. Workspace claim missing on fresh signup — recovers when the
   *      `createPersonalWorkspace` CF refreshes the ID token.
   *   3. Firestore rules bug — would also surface in write operations,
   *      which DO log + propagate (their catchError doesn't go through here).
   *
   * Checking `!authStore.user()?.userUID` is unreliable: the listener can
   * fire its error before the user signal propagates, so race conditions
   * leak the error through. Unconditional silencing is the robust choice.
   */
  protected handleError(error: unknown): Observable<never> {
    if (isPermissionDeniedError(error)) {
      return EMPTY as Observable<never>;
    }
    console.error(`An error occurred in ${this.config.collectionName} service:`, error);
    const message = error instanceof Error ? error.message : 'Database error';
    return throwError(() => new Error(message));
  }

  /**
   * Force reload entities from Firestore
   */
  public reloadEntities(): void {
    this.fetchAllEntities();
  }

  // ========================================
  // ENHANCED FUNCTIONALITY
  // ========================================

  /**
   * Query entities with advanced filtering and pagination
   */
  public queryEntities(options: QueryOptions): Observable<T[]> {
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    const startTime = performance.now();

    try {
      const constraints: QueryConstraint[] = [];
      
      // Add filtering
      if (options.field && options.operator && options.value !== undefined) {
        constraints.push(where(options.field, options.operator, options.value));
      }
      
      // Add ordering
      if (options.orderByField) {
        constraints.push(orderBy(options.orderByField, options.orderDirection || 'asc'));
      }
      
      // Add pagination
      if (options.startAfterDoc) {
        constraints.push(startAfter(options.startAfterDoc));
      }
      
      if (options.endBeforeDoc) {
        constraints.push(endBefore(options.endBeforeDoc));
      }
      
      // Add limit
      if (options.limitCount) {
        constraints.push(limit(options.limitCount));
      }

      const q = query(this.getCollection(), ...constraints);
      
      return collectionData(q, { idField: this.config.idField }).pipe(
        map(data => {
          const entities = data.map(item => {
            const entity = this.config.entityConstructor(item) as T;
            this.parseDateFields(entity as unknown as Record<string, unknown>);
            return entity;
          });
          
          this.recordPerformanceMetric('queryEntities', startTime, entities.length);
          return entities;
        }),
        catchError(error => {
          this.recordPerformanceMetric('queryEntities', startTime, 0, false, error.message);
          return this.handleError(error);
        })
      );
    } catch (error) {
      this.recordPerformanceMetric('queryEntities', startTime, 0, false, (error as Error).message);
      return throwError(() => error);
    }
  }

  /**
   * Get entity count with optional filtering
   */
  public getEntityCountFromServer(options?: QueryOptions): Observable<number> {
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    const startTime = performance.now();

    try {
      const constraints: QueryConstraint[] = [];
      
      // Add filtering
      if (options?.field && options?.operator && options?.value !== undefined) {
        constraints.push(where(options.field, options.operator, options.value));
      }

      const q = query(this.getCollection(), ...constraints);
      
      return from(getCountFromServer(q)).pipe(
        map(snapshot => {
          const count = snapshot.data().count;
          this.recordPerformanceMetric('getEntityCountFromServer', startTime, count);
          return count;
        }),
        catchError(error => {
          this.recordPerformanceMetric('getEntityCountFromServer', startTime, 0, false, error.message);
          return this.handleError(error);
        })
      );
    } catch (error) {
      this.recordPerformanceMetric('getEntityCountFromServer', startTime, 0, false, (error as Error).message);
      return throwError(() => error);
    }
  }

  /**
   * Execute batch operations (create, update, delete multiple entities)
   */
  public executeBatchOperations(operations: BatchOperation<T>[]): Observable<void> {
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    if (operations.length === 0) {
      return of(undefined);
    }

    if (operations.length > 500) {
      return throwError(() => new Error('Batch operations limited to 500 operations'));
    }

    const startTime = performance.now();
    const currentUser = this.authStore.user();
    
    if (!currentUser?.userUID) {
      return throwError(() => new Error('User authentication required'));
    }

    try {
      const batch = writeBatch(this.firestore);
      let originalEntitiesData: T[] = [];

      // Optimistic updates if enabled
      if (this.config.enableOptimisticUpdates) {
        originalEntitiesData = [...this.entitiesData];
        this.applyOptimisticBatchUpdates(operations);
      }

      // Prepare batch operations
      operations.forEach(operation => {
        switch (operation.type) {
          case 'create':
            if (operation.data) {
              const id = operation.id || this.createId();
              const docRef = this.getDocument(id);
              const cleanedData = this.cleanDocumentData({
                ...operation.data,
                [this.config.idField]: id,
                ownerUID: currentUser.userUID
              });
              const timestampedData = this.addTimestamps(cleanedData, false);
              batch.set(docRef, timestampedData);
            }
            break;
            
          case 'update':
            if (operation.id && operation.data) {
              const docRef = this.getDocument(operation.id);
              const cleanedData = this.cleanDocumentData({
                ...operation.data,
                ownerUID: currentUser.userUID
              });
              const timestampedData = this.addTimestamps(cleanedData, true);
              batch.update(docRef, timestampedData);
            }
            break;
            
          case 'delete':
            if (operation.id) {
              const docRef = this.getDocument(operation.id);
              if (this.config.enableSoftDeletes) {
                batch.update(docRef, {
                  deleted: true,
                  deletedAt: new Date()
                });
              } else {
                batch.delete(docRef);
              }
            }
            break;
        }
      });

      return from(batch.commit()).pipe(
        map(() => {
          this.recordPerformanceMetric('executeBatchOperations', startTime, operations.length);
        }),
        catchError(error => {
          // Rollback optimistic updates on error
          if (this.config.enableOptimisticUpdates && originalEntitiesData.length > 0) {
            console.error(`❌ Batch operations failed, rolling back optimistic updates:`, error);
            this.entitiesData = originalEntitiesData;
            this.entitiesSubject.next([...this.entitiesData]);
          }
          this.recordPerformanceMetric('executeBatchOperations', startTime, operations.length, false, error.message);
          return throwError(() => error);
        })
      );
    } catch (error) {
      this.recordPerformanceMetric('executeBatchOperations', startTime, operations.length, false, (error as Error).message);
      return throwError(() => error);
    }
  }

  /**
   * Search entities by text (requires proper indexing in Firestore)
   */
  public searchEntities(searchField: string, searchTerm: string, options?: QueryOptions): Observable<T[]> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return of([]);
    }

    // Use range query for text search (requires proper indexing)
    const searchOptions: QueryOptions = {
      field: searchField,
      operator: '>=',
      value: searchTerm,
      ...options
    };

    return this.queryEntities(searchOptions).pipe(
      map(entities => {
        // Client-side filtering for more precise text matching
        return entities.filter(entity => {
          const fieldValue = (entity as unknown as Record<string, unknown>)[searchField];
          if (typeof fieldValue === 'string') {
            return fieldValue.toLowerCase().includes(searchTerm.toLowerCase());
          }
          return false;
        });
      })
    );
  }

  // ========================================
  // PRIVATE HELPER METHODS FOR ENHANCEMENTS
  // ========================================

  /**
   * Apply optimistic updates for batch operations
   */
  protected applyOptimisticBatchUpdates(operations: BatchOperation<T>[]): void {
    operations.forEach(operation => {
      switch (operation.type) {
        case 'create':
          if (operation.data) {
            const id = operation.id || this.createId();
            const newEntity = this.config.entityConstructor({
              ...operation.data,
              [this.config.idField]: id
            }) as T;
            this.entitiesData.push(newEntity);
          }
          break;
          
        case 'update':
          if (operation.id && operation.data) {
            const entityIndex = this.entitiesData.findIndex(e => (e as unknown as Record<string, unknown>)[this.config.idField] === operation.id);
            if (entityIndex !== -1) {
              this.entitiesData[entityIndex] = Object.assign({}, this.entitiesData[entityIndex], operation.data);
            }
          }
          break;
          
        case 'delete':
          if (operation.id) {
            if (this.config.enableSoftDeletes) {
              const entityIndex = this.entitiesData.findIndex(e => (e as unknown as Record<string, unknown>)[this.config.idField] === operation.id);
              if (entityIndex !== -1) {
                (this.entitiesData[entityIndex] as { deleted?: boolean; deletedAt?: Date | null }).deleted = true;
                (this.entitiesData[entityIndex] as { deleted?: boolean; deletedAt?: Date | null }).deletedAt = new Date();
              }
            } else {
              this.entitiesData = this.entitiesData.filter(e => (e as unknown as Record<string, unknown>)[this.config.idField] !== operation.id);
            }
          }
          break;
      }
    });

    this.entitiesSubject.next([...this.entitiesData]);
  }
}
